if (!customElements.get('restaurant-menu')) {
  customElements.define(
    'restaurant-menu',
    class RestaurantMenu extends HTMLElement {
      constructor() {
        super();
        this.modal = this.querySelector('[data-menu-modal]');
        this.form = this.querySelector('[data-menu-form]');
        this.optionsContainer = this.querySelector('[data-menu-options]');
        this.variantInput = this.querySelector('[data-menu-variant-id]');
        this.addButton = this.querySelector('[data-menu-add]');
        this.addLabel = this.querySelector('[data-menu-add-label]');
        this.message = this.querySelector('[data-menu-message]');
        this.product = null;
        this.trigger = null;

        this.addEventListener('click', this.handleClick.bind(this));
        this.optionsContainer?.addEventListener('change', this.updateVariant.bind(this));
        this.form?.addEventListener('submit', this.addToCart.bind(this));
        this.modal?.addEventListener('click', this.handleBackdropClick.bind(this));
        this.modal?.addEventListener('close', () => this.trigger?.focus());
      }

      handleClick(event) {
        const productButton = event.target.closest('[data-menu-product]');
        if (productButton) {
          this.openProduct(productButton);
          return;
        }

        if (event.target.closest('[data-menu-close]')) this.closeModal();
      }

      handleBackdropClick(event) {
        if (event.target === this.modal) this.closeModal();
      }

      openProduct(button) {
        const dataElement = this.querySelector(`#RestaurantMenuProduct-${button.dataset.menuProduct}`);
        if (!dataElement) return;

        try {
          this.product = JSON.parse(dataElement.textContent);
        } catch (error) {
          console.error('The menu product data could not be read.', error);
          return;
        }

        this.trigger = button;
        this.message.textContent = '';
        this.form.reset();
        this.querySelector('[data-modal-title]').textContent = this.product.title;
        this.querySelector('[data-modal-description]').textContent = this.stripHtml(this.product.description || '');

        const image = this.querySelector('[data-modal-image]');
        const media = this.querySelector('[data-modal-media]');
        if (this.product.featured_image) {
          image.src = this.product.featured_image;
          image.alt = this.product.title;
          media.hidden = false;
        } else {
          image.removeAttribute('src');
          media.hidden = true;
        }

        this.renderOptions();
        this.updateVariant();
        this.modal.showModal();
      }

      renderOptions() {
        this.optionsContainer.innerHTML = '';
        const hasOnlyDefaultVariant = this.product.variants.length === 1 &&
          this.product.variants[0].title === 'Default Title';

        if (hasOnlyDefaultVariant) return;

        this.product.options.forEach((optionName, optionIndex) => {
          const field = document.createElement('label');
          field.className = 'restaurant-menu-field';

          const label = document.createElement('span');
          label.textContent = optionName;

          const select = document.createElement('select');
          select.dataset.optionIndex = optionIndex;
          select.setAttribute('aria-label', optionName);

          const values = [...new Set(this.product.variants.map((variant) => variant.options[optionIndex]))];
          values.forEach((value) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = value;
            select.appendChild(option);
          });

          field.append(label, select);
          this.optionsContainer.appendChild(field);
        });
      }

      updateVariant() {
        if (!this.product) return;

        const selectedOptions = [...this.optionsContainer.querySelectorAll('select')].map((select) => select.value);
        const variant = this.product.variants.find((candidate) =>
          candidate.options.every((option, index) => option === selectedOptions[index])
        ) || this.product.variants[0];

        this.variant = variant;
        this.variantInput.value = variant.id;
        this.querySelector('[data-modal-price]').textContent = this.formatMoney(variant.price);

        const canAdd = Boolean(variant && variant.available);
        this.addButton.disabled = !canAdd;
        this.addButton.setAttribute('aria-disabled', String(!canAdd));
        this.addLabel.textContent = canAdd ? 'Add to order' : 'Sold out';
      }

      async addToCart(event) {
        event.preventDefault();
        if (!this.variant?.available || this.addButton.disabled) return;

        this.setLoading(true);
        this.message.textContent = '';
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData(this.form);

        try {
          await this.validateServiceDay(formData);

          if (cart?.getSectionsToRender) {
            formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            cart.setActiveElement?.(this.trigger);
          }

          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'This item could not be added.');
          }

          this.closeModal(false);
          if (cart?.renderContents && result.sections) {
            cart.classList.remove('is-empty');
            cart.renderContents(result);
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }
        } catch (error) {
          this.message.textContent = error.message;
        } finally {
          this.setLoading(false);
        }
      }

      async validateServiceDay(formData) {
        const requestedDay = String(formData.get('properties[Service day]') || '').trim();
        if (!requestedDay) return;

        const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
        const response = await fetch(cartUrl, {
          method: 'GET',
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) return;
        const currentCart = await response.json();
        const existingDays = [...new Set(
          currentCart.items
            .map((item) => String(item.properties?.['Service day'] || '').trim())
            .filter(Boolean)
        )];

        const conflictingDay = existingDays.find((day) => day.toLowerCase() !== requestedDay.toLowerCase());
        if (conflictingDay) {
          throw new Error(
            `Your cart already contains items for ${conflictingDay}. Complete or clear that order before adding ${requestedDay} items.`
          );
        }
      }

      setLoading(isLoading) {
        this.addButton.classList.toggle('loading', isLoading);
        this.addButton.disabled = isLoading;
        this.addButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      closeModal(returnFocus = true) {
        if (!this.modal.open) return;
        if (!returnFocus) this.trigger = null;
        this.modal.close();
      }

      formatMoney(cents) {
        const amount = (Number(cents) / 100).toFixed(2);
        const format = this.dataset.moneyFormat || '${{amount}}';
        return format
          .replace(/{{\s*amount_no_decimals\s*}}/, Math.round(Number(cents) / 100).toLocaleString())
          .replace(/{{\s*amount\s*}}/, Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 }));
      }

      stripHtml(html) {
        const element = document.createElement('div');
        element.innerHTML = html;
        return element.textContent.trim();
      }
    }
  );
}
